import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Service, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { ResponseApi } from '../../../../core/dtos/response-api';
import {
  HANDLE_HTTP_ERROR_LOCALLY,
  SKIP_SUCCESS_ALERT,
} from '../../../../core/http/http-context-tokens';
import { CanalPublico, textoPrazo } from '../models/canal-publico.model';
import {
  CABECALHO_DESAFIO,
  CAMPO_HONEYPOT,
  MENSAGEM_DESAFIO_RECUSADO,
  ehRecusaDoDesafio,
} from '../../services/desafio-formulario.service';
import {
  AberturaManifestacao,
  ErroOuvidoriaPublica,
  ManifestacaoAbertura,
  ManifestacaoPublica,
} from '../models/manifestacao-publica.model';

const RECURSO_CANAL = `${environment.apiUrl}/ouvidoria/publico/canal`;
const RECURSO_MANIFESTACAO = `${environment.apiUrl}/ouvidoria/publico/manifestacao`;

/** Estado do canal do órgão — governa o que a tela mostra e se o envio é possível. */
export type EstadoCanal = 'inicial' | 'carregando' | 'pronto' | 'sem-canal' | 'falha';

/** Quem chamou: só o 404 muda de sentido entre a abertura e a consulta por protocolo. */
export type ContextoErroOuvidoria = 'abertura' | 'consulta';

const SEM_CANAL = 'A Ouvidoria deste órgão ainda não está recebendo manifestações on-line.';
const LIMITE = 'Muitas tentativas em pouco tempo. Aguarde alguns instantes e tente novamente.';
const INDISPONIVEL = 'Não foi possível registrar a manifestação. Tente novamente em instantes.';
const NAO_ENCONTRADO = 'Protocolo não encontrado.';

/** Erro tratado na tela: o modal genérico do ErrorInterceptor não abre em nenhuma destas chamadas. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/**
 * A superfície **anônima** da Ouvidoria vista pelo portal: o canal do órgão, a abertura da
 * manifestação e a consulta por protocolo.
 *
 * ⚠️ O portal **não sabe nem envia o órgão**. O back-end o resolve pelo host de quem chamou
 * (cabeçalho `Origin`) contra um mapa do `appsettings`, e por isso o GET do canal não tem
 * parâmetro e o `FormData` da abertura nunca escreve `codOrgaoOrg` — lá é campo do sistema, e
 * mandá-lo devolve 400.
 *
 * Fica na raiz, e não no store do wizard: o cache do canal precisa sobreviver ao `providers` do
 * componente do fluxo, que é destruído ao sair da rota e recriado a cada troca de tipo.
 */
@Service()
export class OuvidoriaPublicaService {
  private readonly http = inject(HttpClient);

  private readonly canalAtual = signal<CanalPublico | null>(null);
  private readonly estado = signal<EstadoCanal>('inicial');

  readonly canal = this.canalAtual.asReadonly();
  readonly estadoCanal = this.estado.asReadonly();

  /** As três regras que o canal habilita. Sem canal carregado, nada é oferecido. */
  readonly permiteAnonimato = computed(() => this.canalAtual()?.permiteAnonimato ?? false);
  readonly permiteSigilo = computed(() => this.canalAtual()?.permiteSigiloIdentidade ?? false);
  readonly permiteAnexo = computed(() => this.canalAtual()?.permiteAnexo ?? false);

  /** "30 dias úteis" — o prazo do canal, que substitui as constantes da Lei 13.460 na tela. */
  readonly prazoTexto = computed(() => {
    const canal = this.canalAtual();

    return canal ? textoPrazo(canal.prazoPadrao, canal.unidadePrazo) : '';
  });

  /**
   * Busca o canal uma vez por sessão: os cinco tipos de manifestação são do mesmo órgão, e
   * trocar de `ouvidoria/reclamacao` para `ouvidoria/elogios` não refaz a chamada. Um estado já
   * resolvido (`pronto` ou `sem-canal`) é resposta da API, não falha — só `recarregar` insiste.
   */
  carregarCanal(): void {
    const estado = this.estado();

    if (estado === 'carregando' || estado === 'pronto' || estado === 'sem-canal') return;

    this.buscarCanal();
  }

  /**
   * Marca o canal como ausente sem nova chamada. É o 404 do POST de abertura: o canal saiu do ar
   * entre o carregamento da tela e o envio, e a etapa de Informações precisa dizer isso.
   */
  marcarSemCanal(): void {
    this.canalAtual.set(null);
    this.estado.set('sem-canal');
  }

  /** Força a busca — é o "Tentar novamente" do aviso na etapa de Informações. */
  recarregarCanal(): void {
    if (this.estado() === 'carregando') return;

    this.buscarCanal();
  }

  /**
   * Abre a manifestação. Só o 201 abre a Confirmação, então o erro sobe para o componente
   * decidir a etapa; o alerta automático de sucesso é silenciado porque a Confirmação **é** a
   * resposta ao cidadão.
   *
   * O `desafio` vai no cabeçalho `X-Desafio-Formulario`, nunca no corpo (change
   * anti-abuso-superficie-publica); o `honeypot` só entra no multipart preenchido.
   */
  abrir(
    dados: AberturaManifestacao,
    anexos: readonly File[],
    desafio: string,
    honeypot = '',
  ): Observable<ManifestacaoAbertura> {
    const contexto = ERRO_LOCAL().set(SKIP_SUCCESS_ALERT, true);

    // Sem `Content-Type` de propósito: quem põe o boundary do multipart é o navegador.
    return this.http
      .post<ResponseApi<ManifestacaoAbertura>>(
        RECURSO_MANIFESTACAO,
        montarFormDataAbertura(dados, anexos, honeypot),
        { context: contexto, headers: { [CABECALHO_DESAFIO]: desafio } },
      )
      .pipe(map((resposta) => resposta.data));
  }

  /**
   * Consulta pública por protocolo. O protocolo inteiro é a credencial — não há parâmetro de
   * órgão que permita varredura. Nenhuma tela consome isto ainda: o consumidor é a change da
   * tela de acompanhamento.
   */
  consultar(protocolo: string): Observable<ManifestacaoPublica> {
    const rota = `${RECURSO_MANIFESTACAO}/${encodeURIComponent(protocolo.trim())}`;

    return this.http
      .get<ResponseApi<ManifestacaoPublica>>(rota, { context: ERRO_LOCAL() })
      .pipe(map((resposta) => resposta.data));
  }

  private buscarCanal(): void {
    this.estado.set('carregando');

    this.http
      .get<ResponseApi<CanalPublico>>(RECURSO_CANAL, { context: ERRO_LOCAL() })
      .pipe(
        map((resposta) => resposta.data),
        catchError((erro: unknown) => {
          // 404 é resposta, não falha: o órgão não publica canal, ou o host do portal não está
          // mapeado no back-end. O portal não distingue os dois — nem deve.
          this.estado.set(mapearErro(erro).tipo === 'sem-canal' ? 'sem-canal' : 'falha');

          return throwError(() => erro);
        }),
      )
      .subscribe({
        next: (canal) => {
          this.canalAtual.set(canal);
          this.estado.set('pronto');
        },
        // O estado já foi definido no catchError; aqui só se evita o erro não tratado.
        error: () => undefined,
      });
  }
}

/**
 * Monta o `multipart/form-data` da abertura.
 *
 * Função pura e exportada porque é o ponto em que o contrato do back-end é honrado, e isso
 * merece teste sem HTTP. Três regras que o `FormData` faz cumprir:
 *
 * 1. **só chave com valor** — o back-end trata string vazia como ausente, mas mandar a chave de
 *    um campo não preenchido é ruído no contrato;
 * 2. **nenhum campo do sistema**, `codOrgaoOrg` incluído: lá são propriedades-isca e devolvem
 *    400 nomeando o campo. É o tipo `AberturaManifestacao` que garante isso por construção;
 * 3. **CPF/CNPJ sem máscara** — o modelo guarda mascarado (`NgxMaskDirective` com
 *    `dropSpecialCharacters: false`), o envio manda só os dígitos.
 */
export function montarFormDataAbertura(
  dados: AberturaManifestacao,
  anexos: readonly File[],
  honeypot = '',
): FormData {
  const corpo = new FormData();

  const texto = (chave: string, valor: string | undefined | null): void => {
    const limpo = (valor ?? '').trim();

    if (limpo) corpo.append(chave, limpo);
  };

  const booleano = (chave: string, valor: boolean): void => {
    corpo.append(chave, valor ? 'true' : 'false');
  };

  texto('desTipoManifestacao', dados.desTipoManifestacao);
  texto('desAssunto', dados.desAssunto);
  texto('desDescricao', dados.desDescricao);

  booleano('bitAnonima', dados.bitAnonima);
  booleano('bitSigiloIdentidade', dados.bitSigiloIdentidade);
  booleano('bitDesejaResposta', dados.bitDesejaResposta);
  booleano('bitAceiteComunicacao', dados.bitAceiteComunicacao);
  texto('desFormaResposta', dados.desFormaResposta);

  texto('desNome', dados.desNome);
  texto('desCpfCnpj', apenasDigitos(dados.desCpfCnpj));
  texto('desEmail', dados.desEmail);
  texto('desTelefone', dados.desTelefone);

  texto('desEndereco', dados.desEndereco);
  texto('desBairro', dados.desBairro);
  texto('desCidade', dados.desCidade);
  texto('desUf', dados.desUf);

  // Uma entrada por arquivo, na ordem em que o cidadão os adicionou. Sem arquivo, a chave
  // `anexos` simplesmente não existe.
  for (const arquivo of anexos) {
    corpo.append('anexos', arquivo, arquivo.name);
  }

  // O campo invisível: vazio (o caso de toda pessoa) não vai — a regra 1 vale para ele também.
  texto(CAMPO_HONEYPOT, honeypot);

  return corpo;
}

/**
 * Traduz o erro HTTP para o que a tela decide: para onde levar o cidadão e o que escrever.
 *
 * A `message` da API é preferida sempre que existir — a regra recusada é da ouvidoria de
 * destino, e reescrevê-la aqui esconderia o motivo real do cidadão.
 */
export function mapearErro(
  erro: unknown,
  contexto: ContextoErroOuvidoria = 'abertura',
): ErroOuvidoriaPublica {
  const resposta = erro instanceof HttpErrorResponse ? erro : null;
  const corpo = (resposta?.error ?? null) as Partial<ResponseApi<unknown>> | null;

  const mensagemApi = typeof corpo?.message === 'string' ? corpo.message.trim() : '';

  // O back-end repete a primeira violacao em `message` quando ha uma so: sem este filtro, a tela
  // mostraria a mesma frase duas vezes, no titulo e na lista.
  const detalhes = (Array.isArray(corpo?.errors) ? corpo.errors : [])
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map((item) => item.trim())
    .filter((item) => item !== mensagemApi);

  // O 422 do desafio de formulário: o cidadão só precisa enviar de novo — o portal já pediu
  // outro desafio. Reconhecido pelo cabeçalho da API, nunca pelo texto.
  if (ehRecusaDoDesafio(erro)) {
    return { tipo: 'desafio', mensagem: MENSAGEM_DESAFIO_RECUSADO, detalhes: [] };
  }

  switch (resposta?.status) {
    case 400:
      return {
        tipo: 'validacao',
        mensagem: mensagemApi || 'Não foi possível registrar a manifestação com estes dados.',
        detalhes,
      };

    case 404:
      return contexto === 'consulta'
        ? { tipo: 'nao-encontrado', mensagem: mensagemApi || NAO_ENCONTRADO, detalhes: [] }
        : { tipo: 'sem-canal', mensagem: SEM_CANAL, detalhes: [] };

    case 429:
      // O 429 do back-end vem em texto puro, fora do envelope: ele é do pipeline, antes do
      // MediatR, e não há handler para montar o `ResponseApi`.
      return { tipo: 'limite', mensagem: LIMITE, detalhes: [] };

    default:
      // Status 0 (rede/CORS) e 5xx caem aqui: o cidadão não tem o que corrigir, só tentar de novo.
      return { tipo: 'indisponivel', mensagem: INDISPONIVEL, detalhes: [] };
  }
}

function apenasDigitos(valor: string | undefined | null): string {
  return String(valor ?? '').replace(/\D/g, '');
}
